/**
 * PIN-код и биометрия нашего Flutter-приложения. Экраны ввода, смены и
 * удаления кода и запрос биометрии — у оболочки; страница только просит её
 * открыть нужный и показывает, что она ответила. Сам код сюда не приходит.
 */
import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { keys } from '../../api/keys'
import { isNativeShell, requestNative } from '../../app/nativeBridge'

export interface SecurityStatus {
  hasPin: boolean
  failedAttempts: number
  /** Датчик в телефоне есть. */
  biometricSupported: boolean
  /** В телефоне сохранены лицо или отпечаток. */
  biometricEnrolled: boolean
  biometricAvailable: boolean
  biometricEnabled: boolean
  canEnableBiometrics: boolean
  logoutRequired: boolean
  availableTypes: string[]
  /** Только подпись переключателя: способ входа выбирает телефон. */
  displayMode: 'face' | 'fingerprint' | 'both' | 'generic' | 'unavailable'
}

export type SecurityOperation =
  | { action: 'security.setupPin' | 'security.changePin' | 'security.removePin' }
  | { action: 'security.setBiometricEnabled'; enabled: boolean }

/** Оболочка сообщает, что настройки поменялись не по нашей просьбе. */
export const STATUS_EVENT = 'dorixona-native-security-status-changed'

/**
 * Состояние отвечает сразу, без окон. Не ответила за это время — оболочка
 * старая, без PIN-кода, и раздела не будет.
 */
export const STATUS_TIMEOUT = 5000

const isStatus = (value: unknown): value is SecurityStatus =>
  typeof (value as Partial<SecurityStatus> | null)?.hasPin === 'boolean'

/**
 * Состояние спрашиваем при каждом открытии настроек: PIN-код мог смениться в
 * самой оболочке. Без оболочки запросов нет.
 */
export function useNativeSecurity(customerId: number) {
  const queryClient = useQueryClient()
  const key = keys.nativeSecurity(customerId)

  const status = useQuery({
    queryKey: key,
    queryFn: () => requestNative<SecurityStatus>('security.getStatus', {}, STATUS_TIMEOUT),
    enabled: isNativeShell(),
    refetchOnMount: 'always',
    staleTime: 0,
    retry: false,
  })

  useEffect(() => {
    // Событие только обновляет то, что показано; разблокирует оболочка сама.
    const onChanged = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail
      if (isStatus(detail)) queryClient.setQueryData(keys.nativeSecurity(customerId), detail)
    }
    window.addEventListener(STATUS_EVENT, onChanged)
    return () => {
      window.removeEventListener(STATUS_EVENT, onChanged)
    }
  }, [queryClient, customerId])

  const run = useMutation({
    mutationFn: (operation: SecurityOperation) =>
      requestNative<SecurityStatus>(
        operation.action,
        'enabled' in operation ? { enabled: operation.enabled } : {},
      ),
    onSuccess: (next) => {
      queryClient.setQueryData(key, next)
    },
  })

  return { status: status.data ?? null, run }
}
